import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { markOfferRead } from '../../../../common/api/clients/order.api';
import { queries } from '../../../../common/api/queryKeys/queries';
import { useAbly } from '../../../../common/hooks/useAbly';
import { useRole } from '../../../../common/hooks/useRole';

export const useOfferDetails = (id?: string) => {
    const queryClient = useQueryClient();
    const markOfferReadMutation = useMutation({ mutationFn: markOfferRead });
    const { subscribe, unsubscribe } = useAbly();
    const role = useRole();

    const query = useQuery({
        ...queries.offers.detail(id!),
        enabled: Boolean(id),
    });

    const offer = query.data;

    // Only mark offer as read once after initial fetch
    useEffect(() => {
        if (offer) {
            const companyId = role.isCustomer() ? offer.customerId : offer.supplierId;
            const isRead = role.isCustomer() ? offer.customerRead : offer.supplierRead;

            if (!isRead && id && !role.isSuperAdmin() && role.hasWriteRights(companyId)) {
                markOfferReadMutation.mutate({
                    offerId: id,
                    read: true,
                });
            }
        }
    }, [offer, id]); // eslint-disable-line react-hooks/exhaustive-deps

    const prefetch = (offerId: string) => {
        return queryClient.prefetchQuery(queries.offers.detail(offerId));
    };

    useEffect(() => {
        if (!offer) {
            return;
        }

        const topic = role.isSupplier()
            ? `suppliers:${offer.supplierId}:offers:${offer.id}`
            : `customers:${offer.customerId}:orders:${offer.orderId}:offers:${offer.id}`;

        subscribe(topic, () => {
            query.refetch();
            queryClient.invalidateQueries({ queryKey: queries.offers.detail(offer.id)._ctx.approvalSettings.queryKey });
        });
        return () => {
            unsubscribe(topic);
        };
    }, [offer, role, query, queryClient, subscribe, unsubscribe]);

    return {
        ...query,
        prefetch,
    };
};
